import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import { CHANNEL_DRAWER_SECTIONS, type ChannelDrawerSection } from '@/components/ChannelDrawerRail';
import { useI18nText } from '@/lib/utils/i18nText';

/**
 * Left side of the merged channel drawer's header (see ChannelDrawer.tsx) —
 * `{thumbnail} {platform icon} {channel name} | {section icon} {section label}`.
 * Shared by both panes (Channel details in panel/user.tsx, Chat system in
 * InfluencerChatDrawer.tsx) so the channel identity reads the same wherever the
 * drawer is open, and the header keeps naming the channel regardless of which
 * tab is active or how far the body is scrolled.
 *
 * The section icon/label come from `CHANNEL_DRAWER_SECTIONS`, the same list the
 * rail's buttons render from, so the header and the rail always match.
 *
 * `channelType`/`channelName`/`channelThumbnail` are optional because each pane
 * loads its own channel data independently and may still be loading when this
 * renders — falls back to just the section icon/label until it resolves.
 */
export function ChannelDrawerHeaderTitle({
  section,
  channelType,
  channelName,
  channelThumbnail,
}: {
  section: ChannelDrawerSection;
  channelType?: string;
  channelName?: string;
  channelThumbnail?: string;
}) {
  const { t } = useI18nText();
  const meta = CHANNEL_DRAWER_SECTIONS.find((s) => s.key === section)!;

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {channelName && (
        <>
          <ChannelThumbnail
            src={channelThumbnail}
            className="h-5 w-5 shrink-0 rounded-ggc-pill bg-ggc-bg-base object-cover"
            fallbackClassName="h-5 w-5 shrink-0 rounded-ggc-pill bg-ggc-bg-base flex items-center justify-center"
            referrerPolicy="no-referrer"
          />
          {channelType && <ChannelTypeIcon type={channelType} size={16} />}
          <span className="truncate ggc-text-body-bold">{channelName}</span>
          <span className="px-2 text-sm text-gray-300" aria-hidden>|</span>
        </>
      )}
      <i className={`fa ${meta.icon} ${meta.iconColorClass}`} />
      <span className="truncate ggc-text-section-header">{t(meta.labelEn, meta.labelKo)}</span>
    </span>
  );
}
